'use client';

import { usePathname } from 'next/navigation';
import { countryLanguages, defaultLanguage, LocaleDetails } from 'shared/src/types/languages.type';
import { NotFoundMessage } from './not-found-message';

/**
 * The view rendered by the not-found.tsx boundaries.
 *
 * Next gives a not-found boundary no params and no request context, so unlike
 * the in-route empty-state path this one cannot know which URL failed, cannot
 * run the recovery lookup, and cannot be handed a locale. It reads the locale
 * back out of the pathname instead — the only signal available — so at least
 * the copy is in the visitor's language rather than defaulting the whole
 * fleet to English.
 *
 * Deliberately static: no query prefill and no suggestions. The paths that
 * reach a boundary are the gates (app-origin surface blocks, admin), not dead
 * catalogue URLs — those are handled in-route where the slug is in hand.
 */
const localeFromPathname = (pathname: string | null): LocaleDetails => {
  const first = pathname?.split('/').filter(Boolean)[0];
  return (first && countryLanguages[first]) || defaultLanguage;
};

export const NotFoundBoundary = () => {
  const locale = localeFromPathname(usePathname());

  return (
    <div className="flex min-h-[70vh] flex-col justify-center px-4 py-16">
      <NotFoundMessage locale={locale} />
    </div>
  );
};
